import React from 'react';
import {
  arrayOf, bool, number, shape, string
} from 'prop-types';
import classnames from 'classnames';
import { Tab } from '@thd-olt-component-react/tab';
import { Col, Row } from '@thd-olt-component-react/grid';
import { AttributeList } from '../attribute-list/AttributeList';
import { SizeAndFit } from '../size-and-fit/SizeAndFit';
import { LearnMoreShipping } from '../learn-more/LearnMoreShipping';
import { LearnMoreDelivery } from '../learn-more/LearnMoreDelivery';
import { LearnMoreReturns } from '../learn-more/LearnMoreReturns';
import '../collection-info-tabs.scss';
import { getCareInstructions, getOtherDescriptors } from '../../helpers';

export const CollectionTabs = ({ description, descriptiveAttributes, descriptors, metadata }) => {

  const { brandName = '' } = metadata || {};
  const careInstructions = getCareInstructions({ descriptors });
  const otherDescriptors = getOtherDescriptors({ descriptors, careInstructions });
  const { description: careDescription } = careInstructions || {};
  const SIZE_AND_FIT_BRANDS = [
    'cstudio home',
    'cstudio',
    'the company store'
  ];

  const sizeAndFitEligible = SIZE_AND_FIT_BRANDS.indexOf(brandName.toLowerCase()) > -1;
  const hasDescriptiveAttributes = (descriptiveAttributes || []).length > 0;
  const hasDescriptors = (descriptors || []).length > 0;
  const descriptionContentAvailable = description
    || hasDescriptiveAttributes
    || hasDescriptors;
  const twoTabs = descriptionContentAvailable || sizeAndFitEligible;
  const threeTabs = descriptionContentAvailable && sizeAndFitEligible;

  const classes = classnames('collection-info-tabs', {
    'collection-info-tabs--two-tabs': twoTabs && !threeTabs,
    'collection-info-tabs--three-tabs': threeTabs
  });

  return (
    <div className={classes}>
      <Tab.Group activeIndex={threeTabs ? 0 : 2}>
        {descriptionContentAvailable && (
          <Tab>
            <Tab.Title>Description & Care</Tab.Title>
            <Tab.Content>
              {(hasDescriptiveAttributes || careInstructions) && (
                <Row>
                  <Col md={6} sm={6} lg={6} fallback={12}>
                    {descriptiveAttributes && (
                      <AttributeList attributes={descriptiveAttributes} />
                    )}
                    {description && <p className="collection-info-tabs__description">{description}</p>}
                    {careInstructions && (
                      <AttributeList
                        heading={careInstructions.heading}
                        attributes={careInstructions.descriptiveAttributes}
                        careDescription={careDescription}
                      />
                    )}
                  </Col>
                  <Col md={6} sm={6} lg={6} fallback={12}>
                    {otherDescriptors.length > 0 && (
                      <>
                        {otherDescriptors.map((desc, descIndex) => {
                          return (
                            <AttributeList
                              key={descIndex}
                              attributes={desc.descriptiveAttributes}
                              heading={desc.heading}
                              description={desc.description}
                            />
                          );
                        })}
                      </>
                    )}
                  </Col>
                </Row>
              )}

            </Tab.Content>
          </Tab>
        )}
        {SIZE_AND_FIT_BRANDS.indexOf(brandName.toLowerCase()) > -1 && (
          <Tab>
            <Tab.Title>Size & Fit</Tab.Title>
            <Tab.Content>
              <SizeAndFit />
            </Tab.Content>
          </Tab>
        )}
        <Tab>
          <Tab.Title>Shipping & Returns</Tab.Title>
          <Tab.Content>
            <div className="collection-info-tabs__shipping">
              <LearnMoreShipping />
              <LearnMoreDelivery />
              <LearnMoreReturns />
            </div>
          </Tab.Content>
        </Tab>
      </Tab.Group>
    </div>
  );
};

CollectionTabs.displayName = 'CollectionTabs';

CollectionTabs.propTypes = {
  description: string,
  descriptiveAttributes: arrayOf(shape({
    bulleted: bool, // ignored :shrug:
    name: string, // ignored
    sequence: number,
    value: string
  })),
  descriptors: arrayOf(shape({
    description: string,
    heading: string
  })),
  metadata: shape({
    brandName: string
  })
};

CollectionTabs.defaultProps = {
  description: null,
  descriptiveAttributes: null,
  descriptors: null,
  metadata: null
};
